import { INSTITUTE, SOCIALS, COURSES, WA_DEFAULT, scrollToId } from "@/lib/site";
import { WhatsAppIcon } from "./Reveal";

const QUICK_LINKS = [
  { label: "Home", id: "#home" },
  { label: "About", id: "#about" },
  { label: "Courses", id: "#courses" },
  { label: "Student Work", id: "#student-work" },
  { label: "Contact", id: "#contact" },
];

function SocialIcon({ name }) {
  if (name === "Instagram")
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
        <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
        <circle cx="12" cy="12" r="4.2" />
        <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
      </svg>
    );
  if (name === "Facebook")
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
        <path d="M13.5 21v-7.5h2.52l.48-3H13.5V8.58c0-.87.24-1.46 1.5-1.46h1.6V4.42c-.28-.04-1.23-.12-2.34-.12-2.32 0-3.9 1.41-3.9 4v2.2H7.5v3h2.86V21h3.14Z" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
      <path d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.28 5 12 5 12 5s-6.28 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26.2 26.2 0 0 0 2 12a26.2 26.2 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.72 19 12 19 12 19s6.28 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26.2 26.2 0 0 0 22 12a26.2 26.2 0 0 0-.4-4.8ZM10 15.2V8.8L15.5 12 10 15.2Z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer className="relative border-t border-white/[0.06] bg-obsidian-card/40" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <img
              src="/logo.jpeg"
              alt="Motion Mind Institute logo"
              className="h-11 w-11 rounded-xl object-cover ring-1 ring-white/15"
            />
            <div className="leading-none">
              <p className="font-display font-bold text-white text-sm">
                Motion <span className="text-brand">Mind</span> Institute
              </p>
              <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-amber-500">
                {INSTITUTE.tagline}
              </p>
            </div>
          </div>
          <p className="mt-5 text-sm text-gray-400 leading-relaxed">
            Practical creative education for aspiring designers, editors and digital creators.
          </p>
          <div className="mt-5 flex items-center gap-3" data-testid="footer-socials">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${INSTITUTE.name} on ${s.name}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/12 text-gray-400 hover:border-amber-500/60 hover:text-amber-400 hover:-translate-y-0.5 transition-all duration-300"
                data-testid={`footer-social-${s.name.toLowerCase()}`}
              >
                <SocialIcon name={s.name} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">Quick Links</h3>
          <ul className="mt-5 space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.id}>
                <button
                  onClick={() => scrollToId(l.id)}
                  className="text-sm text-gray-400 hover:text-white hover:pl-1.5 transition-all duration-300"
                  data-testid={`footer-link-${l.label.toLowerCase().replace(" ", "-")}`}
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">Courses</h3>
          <ul className="mt-5 space-y-3">
            {COURSES.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => scrollToId("#courses")}
                  className="text-sm text-gray-400 hover:text-white hover:pl-1.5 transition-all duration-300"
                  data-testid={`footer-course-${c.id}`}
                >
                  {c.title}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">Contact</h3>
          <ul className="mt-5 space-y-3.5 text-sm text-gray-400">
            <li className="leading-relaxed">{INSTITUTE.addressShort}</li>
            <li>
              <a href={`tel:${INSTITUTE.phoneTel}`} className="hover:text-amber-400 transition-colors" data-testid="footer-phone">
                {INSTITUTE.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={`mailto:${INSTITUTE.email}`} className="hover:text-amber-400 transition-colors break-all" data-testid="footer-email">
                {INSTITUTE.email}
              </a>
            </li>
            <li>
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
                data-testid="footer-whatsapp"
              >
                <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/[0.06]">
        <p className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-xs text-gray-500" data-testid="footer-copyright">
          © 2026 {INSTITUTE.name}. All Rights Reserved.
        </p>
      </div>
    </footer>
  );
}
